import React, { useState, useEffect, useContext, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Paper,
  useTheme,
  Container,
  Divider,
} from "@mui/material";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import LoginIcon from "@mui/icons-material/Login";
import RestaurantMenuIcon from "@mui/icons-material/RestaurantMenu";
import UserContext from "../contexts/userContext";
import RecipeDetail from "./RecipeDetail";

const SERVER_URL = process.env.REACT_APP_SERVER_URL;

/**
 * SharedRecipeView - Public recipe page for shared links
 * Handles both authenticated and unauthenticated access
 */
function SharedRecipeView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const { user, session } = useContext(UserContext);
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [recipeData, setRecipeData] = useState(null);
  const [canAccess, setCanAccess] = useState(false);
  const [preview, setPreview] = useState(null);

  const isDark = theme.palette.mode === "dark";

  const fetchRecipe = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const headers = {
        "Content-Type": "application/json",
      };

      // Include auth token if available
      if (session?.access_token) {
        headers["Authorization"] = `Bearer ${session.access_token}`;
      }

      const response = await fetch(
        `${SERVER_URL}/api/recipes/${id}/public`,
        { headers }
      );

      if (!response.ok) {
        throw new Error("Failed to load recipe");
      }

      const data = await response.json();

      if (data.success && data.canAccess) {
        setRecipeData(data.recipe);
        setCanAccess(true);
      } else {
        setCanAccess(false);
        setPreview(data.preview || null);
      }
    } catch (err) {
      console.error("Error fetching shared recipe:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [id, session]);

  useEffect(() => {
    fetchRecipe();
  }, [fetchRecipe]);

  const handleLogin = () => {
    // Store the current URL to redirect back after login
    sessionStorage.setItem("redirectAfterLogin", `/recipe/${id}`);
    navigate("/login");
  };

  // Transform recipe data to match RecipeDetail expected format
  const transformedRecipe = recipeData ? {
    id: recipeData.id,
    title: recipeData.title,
    description: recipeData.description,
    ingredients: recipeData.ingredients || [],
    instructions: recipeData.instructions || [],
    yield: recipeData.yield,
    time: recipeData.time,
    inactive_time: recipeData.inactiveTime,
    path: recipeData.path,
    image_url: recipeData.imageUrl,
    books: recipeData.book ? {
      name: recipeData.book.name,
      author: recipeData.book.author,
    } : null,
    tags: recipeData.tags || [],
  } : null;

  // Update document title and meta tags for SEO/sharing
  useEffect(() => {
    const title = recipeData?.title || preview?.title || "Recipe";
    const description = recipeData?.description || preview?.description || "View this recipe on Reciply";
    const imageUrl = recipeData?.imageUrl || preview?.imageUrl;
    const url = `${window.location.origin}/recipe/${id}`;

    // Update document title
    document.title = `${title} | Reciply`;

    // Helper to set or create meta tag
    const setMetaTag = (property, content, isProperty = true) => {
      if (!content) return;
      const attr = isProperty ? "property" : "name";
      let meta = document.querySelector(`meta[${attr}="${property}"]`);
      if (!meta) {
        meta = document.createElement("meta");
        meta.setAttribute(attr, property);
        document.head.appendChild(meta);
      }
      meta.setAttribute("content", content);
    };

    // Set meta description
    setMetaTag("description", description, false);

    // Open Graph tags
    setMetaTag("og:type", "article");
    setMetaTag("og:title", title);
    setMetaTag("og:description", description);
    setMetaTag("og:url", url);
    if (imageUrl) setMetaTag("og:image", imageUrl);

    // Twitter Card tags
    setMetaTag("twitter:card", "summary_large_image", false);
    setMetaTag("twitter:title", title, false);
    setMetaTag("twitter:description", description, false);
    if (imageUrl) setMetaTag("twitter:image", imageUrl, false);

    // Cleanup on unmount
    return () => {
      document.title = "reciply";
    };
  }, [recipeData, preview, id]);

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "50vh",
        }}
      >
        <CircularProgress sx={{ color: isDark ? "#BB86FC" : "#6366f1" }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="sm">
        <Paper
          sx={{
            p: 4,
            mt: 4,
            textAlign: "center",
            backgroundColor: isDark ? "#1e1e23" : "#fff",
            borderRadius: "16px",
          }}
        >
          <Typography variant="h6" color="error" gutterBottom>
            Unable to load recipe
          </Typography>
          <Typography color="text.secondary">
            {error}
          </Typography>
          <Button
            variant="outlined"
            onClick={() => navigate("/")}
            sx={{ mt: 3 }}
          >
            Go to Home
          </Button>
        </Paper>
      </Container>
    );
  }

  // Full access - show the recipe
  if (canAccess && transformedRecipe) {
    return (
      <Box sx={{ maxWidth: 900, mx: "auto", pb: 4 }}>
        <RecipeDetail
          recipe={transformedRecipe}
          index={0}
          expanded={true}
          disableExpansion={true}
        />
      </Box>
    );
  }

  // Limited access - show preview with login prompt
  return (
    <Container maxWidth="sm">
        <Paper
          sx={{
            mt: 4,
            overflow: "hidden",
            backgroundColor: isDark ? "#1e1e23" : "#fff",
            borderRadius: "20px",
            border: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.05)"}`,
          }}
        >
          {/* Preview Image */}
          {preview?.imageUrl && (
            <Box
              sx={{
                width: "100%",
                height: 250,
                overflow: "hidden",
                position: "relative",
              }}
            >
              <Box
                component="img"
                src={preview.imageUrl}
                alt={preview.title}
                sx={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  filter: "blur(2px) brightness(0.7)",
                }}
              />
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <LockOutlinedIcon
                  sx={{
                    fontSize: 64,
                    color: "rgba(255,255,255,0.8)",
                  }}
                />
              </Box>
            </Box>
          )}

          {/* Content */}
          <Box sx={{ p: 4 }}>
            {/* Title */}
            <Typography
              variant="h5"
              sx={{
                fontFamily: "'JetBrains Mono', monospace",
                fontWeight: 600,
                mb: 1,
                color: theme.palette.text.primary,
              }}
            >
              {preview?.title || "Recipe"}
            </Typography>

            {/* Source */}
            {preview?.bookName && (
              <Typography
                variant="body2"
                sx={{
                  fontFamily: "'JetBrains Mono', monospace",
                  color: isDark ? "#BB86FC" : "#6366f1",
                  fontStyle: "italic",
                  mb: 2,
                }}
              >
                from {preview.bookName}
              </Typography>
            )}

            {/* Description preview */}
            {preview?.description && (
              <Typography
                variant="body2"
                sx={{
                  color: theme.palette.text.secondary,
                  mb: 3,
                  lineHeight: 1.6,
                }}
              >
                {preview.description}
              </Typography>
            )}

            <Divider sx={{ my: 3 }} />

            {/* Login prompt */}
            <Box sx={{ textAlign: "center" }}>
              <RestaurantMenuIcon
                sx={{
                  fontSize: 48,
                  color: isDark ? "#BB86FC" : "#6366f1",
                  opacity: 0.8,
                  mb: 2,
                }}
              />
              
              <Typography
                variant="h6"
                sx={{
                  fontFamily: "'JetBrains Mono', monospace",
                  mb: 1,
                }}
              >
                {user ? "Access Restricted" : "Login to View Full Recipe"}
              </Typography>

              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 3 }}
              >
                {user
                  ? "You don't have permission to view this recipe."
                  : "Sign in to see the complete ingredients and instructions."}
              </Typography>

              {!user && (
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<LoginIcon />}
                  onClick={handleLogin}
                  sx={{
                    backgroundColor: isDark ? "#BB86FC" : "#6366f1",
                    color: isDark ? "#000" : "#fff",
                    fontFamily: "'JetBrains Mono', monospace",
                    textTransform: "lowercase",
                    borderRadius: "12px",
                    px: 4,
                    py: 1.5,
                    "&:hover": {
                      backgroundColor: isDark ? "#9a67ea" : "#4f46e5",
                    },
                  }}
                >
                  sign in
                </Button>
              )}

              <Button
                variant="text"
                onClick={() => navigate("/")}
                sx={{
                  mt: 2,
                  display: "block",
                  mx: "auto",
                  color: theme.palette.text.secondary,
                  fontFamily: "'JetBrains Mono', monospace",
                  textTransform: "lowercase",
                }}
              >
                browse other recipes
              </Button>
            </Box>
          </Box>
        </Paper>
      </Container>
  );
}

export default SharedRecipeView;
